'use client';

import type { Blur64ImageData } from 'blur64';
import { useTranslations } from 'next-intl';
import { ComponentPropsWithoutRef, useEffect, useRef, useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { MediaImage } from './media-image';
import './media.scss';

type Props = {
    src: string;
    alt: string;
    isInView?: boolean;
    posterUrl?: string;
    posterBlurData?: Blur64ImageData;
    priority?: boolean;
    posterProps?: ComponentPropsWithoutRef<typeof MediaImage>;
} & ComponentPropsWithoutRef<'video'>;

export const MediaVideo = ({
    src,
    isInView = true,
    posterUrl,
    alt,
    posterBlurData,
    priority = false,
    posterProps,
    className,
    ...props
}: Props) => {
    const t = useTranslations();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [shouldLoad, setShouldLoad] = useState(priority);

    // Lazy-load via parent: video is `display: none` while loading, so IO on it never intersects.
    useEffect(() => {
        if (shouldLoad) return;
        const el = videoRef.current?.parentElement;
        if (!el) return;

        if (typeof IntersectionObserver === 'undefined') {
            setShouldLoad(true);
            return;
        }

        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    setShouldLoad(true);
                    io.disconnect();
                }
            },
            { rootMargin: '200px' },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [shouldLoad]);

    useEffect(() => {
        if (!shouldLoad || !videoRef.current) return;
        videoRef.current.src = src;
        videoRef.current.load();
    }, [src, shouldLoad]);

    useEffect(() => {
        if (!videoRef.current || isLoading || !shouldLoad) return;
        if (isInView) {
            void videoRef.current.play();
        } else {
            videoRef.current.pause();
        }
    }, [isInView, isLoading, shouldLoad]);

    return (
        <>
            {posterUrl && isLoading && (
                <MediaImage
                    src={posterUrl}
                    alt={alt}
                    blurDataURL={posterBlurData?.blurDataURL}
                    width={posterBlurData?.width}
                    height={posterBlurData?.height}
                    priority={priority}
                    data-testid="media-video-thumbnail"
                    className={cn('media', className)}
                    {...posterProps}
                />
            )}
            <video
                ref={videoRef}
                muted
                loop
                playsInline
                preload={shouldLoad ? 'auto' : 'none'}
                onLoadedData={() => {
                    setIsLoading(false);
                }}
                aria-label={alt}
                data-testid="media-video"
                {...props}
                className={cn('media', isLoading && 'media--hidden', className)}
            >
                {t('ERRORS.VIDEO_NOT_SUPPORTED')}
            </video>
        </>
    );
};
